import Uppy from "@uppy/core";
import Tus from "@uppy/tus/lib";
import cuid from "cuid";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { createLogger } from "@/logger";
import { sendContentEvent } from "@/models/contentEvents";

const log = createLogger("upload");

import { ampli } from "@/config";
import { AppContext } from "@/models/AppStateProvider";
import {
  buildFileMetaData,
  extractFileExtension,
  fetchUploadConfig,
  inferExtensionFromMimeType,
  MISSING_EXTENSION_MESSAGE,
  renameFileWithExtension,
} from "@/utils";
import { fileUploadConfig } from "@/utils/config";
import { publishToWorkspaceFeed } from "./actions/clientActions";
import { DataContext } from "./DataProvider";
import { CurrentFeedContext } from "./StateProviders/currentFeedProvider";
import { MyAccountContext } from "./StateProviders/myAccountProvider";
import { WorkspaceContext } from "./StateProviders/workspaceProvider";

type UppyState = {
  acceptedTypes?: any[];
  addedFile?: any;
  addMetaData: (metadata: any) => void;
  removeMetaData: (key: string) => void;
  hasFiles?: boolean;
  isAudioFileUpload?: boolean;
  isScheduledUpload?: boolean;
  note?: string;
  removeFiles: () => void;
  removePlugin: (pluginName: string) => void;
  setIsScheduledUpload: (scheduled: boolean) => void;
  uppyClient?: Uppy;
  uploading?: boolean;
  uploadStart?: Date | null;
  uploadUrl?: string;
};

interface Restrictions {
  maxNumberOfFiles?: number;
  minNumberOfFiles?: number;
  allowedFileTypes?: string[];
}

let uppyInstance: Uppy;

export const UppyContext = createContext<UppyState>({
  addMetaData: () => {},
  removeMetaData: () => {},
  removeFiles: () => {},
  removePlugin: () => {},
  setIsScheduledUpload: () => {},
});

const UppyContextProvider = ({ children }) => {
  const [uploadUrl, setUploadUrl] = useState<string>("");
  const [uploadStart, setUploadStart] = useState<Date | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [hasFiles, setHasFiles] = useState<boolean>(false);
  const [tempContentId, setTempContentId] = useState<string | null>(null);
  const [addedFile, setAddedFile] = useState<any>();
  const isAudioFileUpload = useMemo(() => addedFile?.meta?.type === "audio", [addedFile]);
  const [isScheduledUpload, setIsScheduledUpload] = useState<boolean>(false);
  const { config } = useContext(AppContext);
  const { preferredLanguage } = useContext(DataContext);
  const { currentWorkspaceId } = useContext(WorkspaceContext);
  const { myAccountId } = useContext(MyAccountContext);
  const { stage, tusUrl } = fetchUploadConfig(config);
  const { currentFeedId: feedId } = useContext(CurrentFeedContext);
  const workspaceId = currentWorkspaceId;

  const acceptedTypes = fileUploadConfig?.acceptedTypes?.length
    ? fileUploadConfig?.acceptedTypes.map((type) => `.${type}`)
    : [];
  const note = fileUploadConfig?.acceptedTypes?.length ? `Allowed Types: ${acceptedTypes.join(", ")}` : "";

  const defaultRestrictions = { minNumberOfFiles: 1 };
  const restrictions = defaultRestrictions as Restrictions;
  restrictions.maxNumberOfFiles = fileUploadConfig?.multiple === true ? 10 : 1;
  if (acceptedTypes?.length > 0) {
    restrictions.allowedFileTypes = acceptedTypes;
  }

  const handleFileAdded = async (file) => {
    log.debug("File added", file);
    setHasFiles(true);
    const setTime = new Date();

    const newContentId = cuid();
    setTempContentId(() => newContentId);
    const itemId = cuid();
    sendContentEvent({
      contentId: newContentId,
      workspaceId: currentWorkspaceId,
      step: "client_processing",
      status: "started",
      context: {
        type: "file",
      },
      feedId,
    }).then(() => {});

    const fileMeta = await buildFileMetaData({
      file,
      stage,
      feedId,
      itemId,
      myAccount: myAccountId,
      uniqueUploadId: newContentId,
    });

    setUploadStart(setTime);

    file.meta = {
      ...file.meta,
      ...fileMeta,
      contentId: newContentId,
      startTime: setTime,
      preferredLanguage,
      inputType: "File",
      // default all uploads to silent
      isSilent: true,
    };
    setAddedFile(file);

    sendContentEvent({
      workspaceId: currentWorkspaceId,
      contentId: newContentId,
      step: "client_uploading",
      status: "started",
      context: {
        type: "file",
      },
      feedId,
    }).then(() => {});

    ampli.fileUploadPhotoGallery();
    return file;
  };

  const handleUploadSuccess = async (file, response) => {
    const contentId = file?.meta?.contentId as string;
    const itemId = file?.meta?.feedItemId as string;
    setTempContentId(() => null);
    if (response?.uploadURL && contentId) {
      try {
        if (file?.meta?.feedId) {
          await publishToWorkspaceFeed({
            workspaceId,
            feedId,
            contentId,
            itemId,
            isSilent: true,
          });
        }
        uppyInstance.removeFile(file.id);
      } catch (e) {
        sendContentEvent({
          workspaceId: currentWorkspaceId,
          contentId,
          step: "client_uploading",
          status: "failed",
          context: {
            type: "file",
          },
          feedId,
          error: e,
        }).then(() => {});
        log.error("Error during file publish", e);
      } finally {
        setHasFiles(false);
        setAddedFile(null);
        setUploading(false);
        setUploadUrl("");
        setUploadStart(null);
        setIsScheduledUpload(false);
        const duration = (new Date().getTime() - new Date(file?.meta?.startTime?.toString()).getTime()) / 1000;

        sendContentEvent({
          contentId,
          workspaceId: currentWorkspaceId,
          step: "client_uploading",
          status: "finished",
          context: {
            type: "file",
            duration,
          },
          feedId,
        }).then(() => {});
      }
    }
  };

  const addMetaData = (metadata: any) => {
    const fileId = addedFile?.id;
    if (!metadata || !fileId || !uppyInstance?.getFile(fileId)) return;

    uppyInstance.setFileMeta(fileId, { ...metadata });
    setAddedFile(uppyInstance.getFile(fileId));
  };

  const removeMetaData = (key: string) => {
    const fileId = addedFile?.id;
    if (!fileId || !uppyInstance) return;

    const updatedFiles = { ...uppyInstance.getState().files };
    const existingFile = updatedFiles[fileId];
    if (!existingFile) return;

    const meta = { ...existingFile.meta };
    delete meta[key];
    updatedFiles[fileId] = { ...existingFile, meta };
    uppyInstance.setState({ files: updatedFiles });
    setAddedFile(uppyInstance.getFile(fileId));
  };

  const removeFiles = () => {
    if (uppyInstance) {
      const files = uppyInstance?.getFiles();
      if (files?.length > 0) {
        files.forEach((file) => uppyInstance.removeFile(file.id));
        setHasFiles(false);
        setAddedFile(null);
      }
    }
  };

  const removePlugin = (pluginName: string) => {
    const plugin = uppyInstance?.getPlugin(pluginName);
    if (plugin) {
      uppyInstance?.removePlugin(plugin);
    }
  };

  useEffect(() => {
    uppyInstance = new Uppy({
      autoProceed: false,
      debug: true,
      restrictions,
      onBeforeFileAdded: (file, files) => {
        if (Object.hasOwn(files, file.id)) {
          return false;
        }
        const data = file?.data as File | Blob | undefined;
        const filename = (file?.name as string | undefined) ?? (data instanceof File ? data.name : "");
        if (extractFileExtension(filename)) {
          return true;
        }
        const inferred = inferExtensionFromMimeType((file?.type as string | undefined) ?? "");
        if (!inferred) {
          uppyInstance?.info(MISSING_EXTENSION_MESSAGE, "error", 5000);
          return false;
        }
        const renamedName = `${filename}.${inferred}`;
        if (data instanceof File) {
          return { ...file, name: renamedName, data: renameFileWithExtension(data, inferred) };
        }
        return { ...file, name: renamedName };
      },
      onBeforeUpload: (file) => {
        ampli.fileUploadSubmit();
        return file;
      },
    }).use(Tus, {
      endpoint: tusUrl,
      removeFingerprintOnSuccess: true,
      retryDelays: [0, 1000, 3000, 5000],
      limit: 1,
    });

    uppyInstance?.on("file-added", handleFileAdded);

    uppyInstance?.on("file-removed", (file) => {
      log.debug("Removed file", file);
    });

    uppyInstance?.on("upload-success", handleUploadSuccess);

    uppyInstance?.on("upload-progress", () => {
      setUploading(true);
    });

    uppyInstance?.on("error", (error) => {
      console.error("ERROR", error);
      sendContentEvent({
        workspaceId: currentWorkspaceId,
        contentId: tempContentId,
        step: "client_uploading",
        status: "failed",
        context: {
          type: "file",
        },
        feedId,
        error: error,
      })?.then(() => {
        setTempContentId(() => null);
        ampli.fileUploadError();
      });
    });

    return () => {
      try {
        uppyInstance?.destroy();
      } catch {
        // instance may already be destroyed
      }
    };
  }, [preferredLanguage, myAccountId, workspaceId, feedId]);

  const uppyState: UppyState = {
    acceptedTypes,
    addedFile,
    addMetaData,
    removeMetaData,
    hasFiles,
    isAudioFileUpload,
    isScheduledUpload,
    note,
    removeFiles,
    removePlugin,
    setIsScheduledUpload,
    uppyClient: uppyInstance,
    uploading,
    uploadStart,
    uploadUrl,
  };

  return <UppyContext.Provider value={uppyState}>{children}</UppyContext.Provider>;
};
export default UppyContextProvider;
